import React, { useRef, useEffect } from 'react';
import { useChat } from './hooks/useChat';
import MessageItem from './components/MessageItem';
import InputArea from './components/InputArea';
import InlineInput from './components/InlineInput';
import { motion, AnimatePresence } from 'framer-motion';

function App() {
  const { messages, isLoading, sendMessage, emailRequested, emailProvided, submitEmail } = useChat();

  const mainRef = useRef(null);
  const inputContainerRef = useRef(null);
  
  // Tracks which phase of resizing we are in
  // 0: Initial, 1: User sent message, 2: AI replied
  const resizeStep = useRef(0);

  // Only the initial AI greeting exists, no user messages yet
  const isInitialState = messages.length === 1 && messages[0].role === 'ai';

  const triggerResize = (minHeight = 0) => {
    // Wait briefly for Framer Motion animations and DOM layout to settle
    setTimeout(() => {
      if (mainRef.current && inputContainerRef.current) {
        const mainHeight = mainRef.current.scrollHeight;
        const inputHeight = inputContainerRef.current.offsetHeight;
        
        // Calculate total desired height safely
        let targetHeight = mainHeight + inputHeight;
        
        // Apply minimum height if specified (e.g. 550px)
        if (minHeight > 0) {
          targetHeight = Math.max(targetHeight, minHeight);
        }
        
        // Post message to parent window to resize the iframe
        window.parent.postMessage(
          { type: 'resize-iframe', height: targetHeight },
          '*'
        );
      }
    }, 500);
  };

  useEffect(() => {
    // 1. User hits send for the first time (messages length is 2: Greeting, User)
    if (resizeStep.current === 0 && messages.length === 2 && isLoading) {
      resizeStep.current = 1;
      triggerResize();
    }
    // 2. AI sends the first reply (messages length is 3: Greeting, User, AI)
    else if (resizeStep.current === 1 && messages.length === 3 && !isLoading) {
      resizeStep.current = 2;
      triggerResize(550); // Enforce minimum height of 550px after reply
    }
  }, [messages.length, isLoading]);

  return (
    <div className="flex flex-col h-screen bg-white font-sans overflow-hidden">
      <AnimatePresence mode="wait">
        {isInitialState ? (
          /* ── INITIAL STATE: greeting + inline underline input ── */
          <motion.main
            key="initial"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
            className="flex-1 flex flex-col justify-center px-6 py-8"
          >
            <div className="max-w-xl w-full mx-auto">
              {/* Greeting text */}
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.4 }}
                className="text-[15px] text-gray-600 leading-relaxed mb-8"
              >
                {messages[0].content}
              </motion.p>
              
              {/* Inline underline input */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.4 }}
              >
                <InlineInput onSend={sendMessage} disabled={isLoading} />
              </motion.div>
            </div>
          </motion.main>
        ) : (
          /* ── CONVERSATION STATE ── */
          <motion.main
            key="chat"
            ref={mainRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="flex-1 overflow-y-auto scroll-smooth"
          >
            <div className="max-w-3xl mx-auto px-4 pt-6 pb-2">
              {messages.map((msg) => (
                <MessageItem key={msg.id} message={msg} />
              ))}
              
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start mb-5"
                >
                  <div className="flex gap-1.5 py-3 items-center">
                    {[0, 150, 300].map((delay, i) => (
                      <div
                        key={i}
                        className="w-2 h-2 rounded-full animate-bounce"
                        style={{
                          backgroundColor: '#543e18',
                          opacity: 0.5 + i * 0.2,
                          animationDelay: `${delay}ms`
                        }}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
              <div className="h-2" />
            </div>
          </motion.main>
        )}
      </AnimatePresence>

      {/* Bottom input — only shown in conversation state */}
      {!isInitialState && (
        <div ref={inputContainerRef}>
          <InputArea
            onSend={sendMessage}
            disabled={isLoading}
            requireEmail={emailRequested && !emailProvided}
            onSubmitEmail={submitEmail}
          />
        </div>
      )}
    </div>
  );
}

export default App;